import { ReactNode } from "react";
import cn from "classnames";
import { ShipmentType } from "entities/shared/shipmentCard.types";
import { searchFilterStore } from "entities/SearchFilter";
import { ShipmentTypeChecker } from "entities/shared/utils";
import { useAppSelector } from "shared/lib/redux";
import { ValueOf } from "shared/lib/typeCheck.lib";
import { useTranslation } from "shared/localization/localization";
import { Link } from "shared/ui/Button";
import { ParcelTrackingIcon, RailTrackingIcon, RoadTrackingIcon } from "shared/ui/Icons";
import {
  INTEGRATION_AIR_TRACKING_URL,
  INTEGRATION_CONTAINER_TRACKING_URL,
  INTEGRATION_GLOBAL_TRACKING_URL,
  isAppIntegration,
  PRICING_AIR_TRACKING_URL,
  PRICING_CONTAINER_TRACKING_URL,
  PRICING_GLOBAL_TRACKING_URL,
  REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
} from "shared/utils/url.utils";
import AirCubeImage from "shared/assets/images/intro/airCube.png";
import AirDecorBottomDark from "shared/assets/images/intro/airDecorBottomDark.png";
import AirDecorBottomLight from "shared/assets/images/intro/airDecorBottomLight.png";
import AirDecorTopDark from "shared/assets/images/intro/airDecorTopDark.png";
import AirDecorTopLight from "shared/assets/images/intro/airDecorTopLight.png";
import AirTrackingImage from "shared/assets/images/intro/airTracking.png";
import ContainerTrackingImage from "shared/assets/images/intro/containerTracking.png";
import GlobalCubeImage from "shared/assets/images/intro/globalCube.png";
import GlobalDecorBottomDark from "shared/assets/images/intro/globalDecorBottomDark.png";
import GlobalDecorBottomLight from "shared/assets/images/intro/globalDecorBottomLight.png";
import GlobalDecorTopDark from "shared/assets/images/intro/globalDecorTopDark.png";
import GlobalDecorTopLight from "shared/assets/images/intro/globalDecorTopLight.png";
import SeaCubeImage from "shared/assets/images/intro/seaCube.png";
import SeaDecorBottomDark from "shared/assets/images/intro/seaDecorBottomDark.png";
import SeaDecorBottomLight from "shared/assets/images/intro/seaDecorBottomLight.png";
import SeaDecorTopDark from "shared/assets/images/intro/seaDecorTopDark.png";
import SeaDecorTopLight from "shared/assets/images/intro/seaDecorTopLight.png";
import styles from "./IntroView.module.scss";
// import { DataLayersManager } from "components/App/App.utils";

type IntroDecorProps = {
  cubeSrc: string;
  topLightSrc: string;
  topDarkSrc: string;
  bottomLightSrc: string;
  bottomDarkSrc: string;
};

const IntroDecor: RFC<IntroDecorProps> = ({ cubeSrc, topLightSrc, topDarkSrc, bottomLightSrc, bottomDarkSrc }) => (
  <>
    <div className={styles.introDecorTopStage}>
      <img className={cn(styles.introDecor, styles.introDecorLight)} src={topLightSrc} alt="" />
      <img className={cn(styles.introDecor, styles.introDecorDark)} src={topDarkSrc} alt="" />
      <div className={styles.introCubeBox}>
        <img src={cubeSrc} alt="" />
      </div>
    </div>
    <div className={styles.introDecorBottomStage}>
      <img className={cn(styles.introDecor, styles.introDecorLight)} src={bottomLightSrc} alt="" />
      <img className={cn(styles.introDecor, styles.introDecorDark)} src={bottomDarkSrc} alt="" />
    </div>
  </>
);

const introViewLogoMap: Record<ValueOf<typeof ShipmentType>, ReactNode> = {
  [ShipmentType.RAIL]: <RailTrackingIcon />,
  [ShipmentType.AIR]: <img src={AirTrackingImage} alt="Air Tracking" />,
  [ShipmentType.SEA]: <img src={ContainerTrackingImage} alt="Container Tracking" />,
  [ShipmentType.ROAD]: <RoadTrackingIcon />,
  [ShipmentType.PARCEL]: <ParcelTrackingIcon />,
};

// const requestItQuoteUrlMap: Record<ValueOf<typeof ShipmentType>, string> = {
//   [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
//   [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
//   [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
//   [ShipmentType.AIR]: REQUEST_IT_QUOTE_AIR_TRACKING_URL,
//   [ShipmentType.SEA]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
// };

const integrateUrlMap: Record<ValueOf<typeof ShipmentType>, string> = {
  [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.AIR]: INTEGRATION_AIR_TRACKING_URL,
  [ShipmentType.SEA]: INTEGRATION_CONTAINER_TRACKING_URL,
};

const pricingUrlMap: Record<ValueOf<typeof ShipmentType>, string> = {
  [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.AIR]: PRICING_AIR_TRACKING_URL,
  [ShipmentType.SEA]: PRICING_CONTAINER_TRACKING_URL,
};

const introViewTitleMap: Record<ValueOf<typeof ShipmentType>, string> = {
  [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.AIR]: "Air Tracking",
  [ShipmentType.SEA]: "Container Tracking",
};

const introViewTextFirstMap: Record<ValueOf<typeof ShipmentType>, string> = {
  [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.AIR]: "Track air shipments with live status and ETA",
  [ShipmentType.SEA]: "Real-time container tracking and smart notifications",
};

const introViewTextSecondMap: Record<ValueOf<typeof ShipmentType>, string> = {
  [ShipmentType.RAIL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.ROAD]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.PARCEL]: REQUEST_IT_QUOTE_CONTAINER_TRACKING_URL,
  [ShipmentType.AIR]:
    "Add the Air Tracking widget to your website with Web Integration, or connect flight and shipment tracking directly to your TMS, ERP, customer portal, or internal system via API.",
  [ShipmentType.SEA]:
    "Add the tracking widget to your website with Web Integration, or connect Qubictron directly to your TMS, ERP, customer portal, or internal system via API.",
};

export const IntroView: RFC = () => {
  const { t } = useTranslation();
  const shipmentType = useAppSelector(searchFilterStore.selectors.shipment);
  const isShipmentTypeConfirmed = useAppSelector(searchFilterStore.selectors.isShipmentTypeConfirmed);
  const isGlobalTracking = useAppSelector(searchFilterStore.selectors.isGlobalTracking);
  const showGlobalTrackingIntro = isGlobalTracking && !isShipmentTypeConfirmed;
  const isSeaIntro = !showGlobalTrackingIntro && ShipmentTypeChecker.isSea(shipmentType);
  const isAirIntro = !showGlobalTrackingIntro && ShipmentTypeChecker.isAir(shipmentType);
  const hasIntroDecor = showGlobalTrackingIntro || isSeaIntro || isAirIntro;

  return (
    <div className={styles.introViewWrapper}>
      <div
        className={cn(
          styles.introView,
          showGlobalTrackingIntro && styles.globalIntro,
          isSeaIntro && styles.seaIntro,
          isAirIntro && styles.airIntro
        )}
      >
        {showGlobalTrackingIntro && (
          <IntroDecor
            cubeSrc={GlobalCubeImage}
            topLightSrc={GlobalDecorTopLight}
            topDarkSrc={GlobalDecorTopDark}
            bottomLightSrc={GlobalDecorBottomLight}
            bottomDarkSrc={GlobalDecorBottomDark}
          />
        )}
        {isSeaIntro && (
          <IntroDecor
            cubeSrc={SeaCubeImage}
            topLightSrc={SeaDecorTopLight}
            topDarkSrc={SeaDecorTopDark}
            bottomLightSrc={SeaDecorBottomLight}
            bottomDarkSrc={SeaDecorBottomDark}
          />
        )}
        {isAirIntro && (
          <IntroDecor
            cubeSrc={AirCubeImage}
            topLightSrc={AirDecorTopLight}
            topDarkSrc={AirDecorTopDark}
            bottomLightSrc={AirDecorBottomLight}
            bottomDarkSrc={AirDecorBottomDark}
          />
        )}
        <div className={styles.contentBox}>
          {!hasIntroDecor && <div className={styles.iconBox}>{introViewLogoMap[shipmentType]}</div>}

          <div>
            {showGlobalTrackingIntro ? <h3>{t("Global Tracking")}</h3> : <h3>{t(introViewTitleMap[shipmentType])}</h3>}

            {showGlobalTrackingIntro ? (
              <p className={styles.introViewText}>{t("Track air and sea shipments in one place")}</p>
            ) : (
              <p className={styles.introViewText}>{t(introViewTextFirstMap[shipmentType])}</p>
            )}
          </div>

          {showGlobalTrackingIntro ? (
            <p className={styles.SecontText}>
              {t(
                "Add the Global Tracking widget to your website with Web Integration, or connect air and ocean tracking directly to your TMS, ERP, customer portal, or internal system via API."
              )}
            </p>
          ) : (
            <p className={styles.SecontText}>{t(introViewTextSecondMap[shipmentType])}</p>
          )}
          {!isAppIntegration && (
            <div className={styles.buttonBox}>
              <Link
                data-test-id="view-plans-pricing-link"
                href={showGlobalTrackingIntro ? PRICING_GLOBAL_TRACKING_URL : pricingUrlMap[shipmentType]}
                target="_blank"
                type="button"
                size="medium"
                className={cn(
                  styles.buttonExploreFeatures,
                  styles.buttonBoxItem,
                  showGlobalTrackingIntro && styles.globalPrimaryButton,
                  isSeaIntro && styles.seaPrimaryButton,
                  isAirIntro && styles.airPrimaryButton
                )}
              >
                {t("View Plans & Pricing")}
              </Link>
              <Link
                data-test-id="view-plans-pricing-link"
                href={showGlobalTrackingIntro ? INTEGRATION_GLOBAL_TRACKING_URL : integrateUrlMap[shipmentType]}
                target="_blank"
                type="button"
                size="medium"
                className={cn(
                  styles.buttonExploreFeatures,
                  styles.buttonBoxItem,
                  showGlobalTrackingIntro && styles.globalSecondaryButton,
                  isSeaIntro && styles.seaSecondaryButton,
                  isAirIntro && styles.airSecondaryButton
                )}
              >
                {t("Integrate Tracking")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
